import { Box, Popover } from "@mantine/core";
import { DropdownGroup, DropdownMenu, DropdownRow } from "@onengine/component-library";
import { IconChevronDown } from "@tabler/icons-react";
import { useState } from "react";
import inputStyles from "./GroupedSelectInput.module.scss";

type Item = {
  value: string;
  label: string;
  icon: React.ReactElement;
  disabled?: boolean;
};

type Group = {
  label: string;
  items: Item[];
};

function GroupedSelectInput({
  label,
  placeholder = "Select…",
  groups,
  value,
  onChange,
  required,
}: {
  label?: string;
  placeholder?: string;
  groups: Group[];
  value: string | null;
  onChange: (v: string | null) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel = groups.flatMap((g) => g.items).find((i) => i.value === value)?.label;

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      position="bottom-start"
      offset={6}
      shadow="md"
      withinPortal
      middlewares={{ flip: true, shift: true }}
      width="target"
    >
      <Popover.Target>
        <Box className={inputStyles.wrapper}>
          {label && (
            <Box className={inputStyles.labelRow}>
              <span className={inputStyles.labelText}>{label}</span>
              {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
            </Box>
          )}

          <Box
            component="button"
            type="button"
            className={`${inputStyles.trigger} ${inputStyles.triggerSmall} ${open ? inputStyles.triggerOpen : ""}`}
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span
              className={`${inputStyles.value} ${!selectedLabel ? inputStyles.placeholder : ""}`}
            >
              <span className={inputStyles.valueText}>{selectedLabel ?? placeholder}</span>
            </span>
            <span className={inputStyles.chevron}>
              <IconChevronDown size={16} />
            </span>
          </Box>
        </Box>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ background: "none", border: "none" }}>
        <DropdownMenu width="100%" scrollable>
          {groups.map((group, i) => (
            <DropdownGroup key={group.label} label={group.label} showDivider={i > 0}>
              {group.items.map((item) => (
                <DropdownRow
                  key={item.value}
                  leftSection={item.icon}
                  title={item.label}
                  active={item.value === value}
                  disabled={item.disabled}
                  onClick={() => {
                    onChange(item.value === value ? null : item.value);
                    setOpen(false);
                  }}
                />
              ))}
            </DropdownGroup>
          ))}
        </DropdownMenu>
      </Popover.Dropdown>
    </Popover>
  );
}

export default GroupedSelectInput;
